<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { computed } from 'vue'

import logoCollapsed from '@/app/assets/logo_color_collapsed.svg'
import { useCurrentSoftware } from '@/shared/composables/useCurrentSoftware'

import AppNavbar from './components/AppNavbar.vue'
import NavAccountCard from './components/NavAccountCard.vue'
import { useShellMenu } from './components/useShellMenu'
import { useSessionChrome } from './useSessionChrome'

/**
 * The AI-mode frame (`meta.layout: 'ai'`, docs/ai-mode/wp/AI-1.md): the floating navbar over the page — there is no
 * vertical nav, so the brand mark takes the menu toggle's place and the nav's account card (profile, role switch, help,
 * logout), avatar only, closes the row; no breadcrumb, footer, assistant pill or scroll-to-top.
 * The frame is exactly the viewport and never scrolls: the page area is a flex column that takes the height left under
 * the navbar (inside the same side gutters), and the page's root element fills it (`flex: 1; min-height: 0`), so the
 * page scrolls its own panels. On mount it loads what the vertical layout loads (`useSessionChrome`).
 */
const xl = useMediaQuery('(min-width: 1200px)')
const lg = useMediaQuery('(min-width: 992px)')
const { software } = useCurrentSoftware()
/** Organization profile and settings, as the vertical nav's account card lists them. */
const menu = useShellMenu()
const accountItems = computed(() => menu.value.filter((item) => item.section === 'account'))

useSessionChrome()
</script>

<template>
  <div class="layout-ai" :class="{ 'is-xl': xl }" data-layout="ai">
    <div class="layout-ai__navbar">
      <AppNavbar :xl="xl" :lg="lg">
        <template #start>
          <RouterLink to="/" class="layout-ai__brand" data-testid="ai-layout-brand">
            <img :src="logoCollapsed" alt="Skileo" />
          </RouterLink>
        </template>
        <template #end>
          <NavAccountCard :items="accountItems" :software="software ?? 're'" mini class="layout-ai__account" />
        </template>
      </AppNavbar>
    </div>
    <main class="layout-ai__page" data-app-content>
      <slot />
    </main>
  </div>
</template>

<style scoped>
.layout-ai {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}
.layout-ai__navbar {
  position: relative;
  z-index: 12;
  flex: 0 0 auto;
  padding: 1.3rem 1.2rem 0;
}
.layout-ai.is-xl .layout-ai__navbar {
  padding: 1.3rem 2rem 0;
}
.layout-ai__brand {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
}
.layout-ai__brand img {
  display: block;
  height: 2rem;
}
.layout-ai .layout-ai__account.is-mini {
  padding: 0;
}
/* In the bar the card is the avatar alone, as round as the bar's other buttons. */
.layout-ai__account :deep(.nav-account__card) {
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 50%;
}
.layout-ai__page {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 1.3rem 1.2rem;
}
.layout-ai.is-xl .layout-ai__page {
  padding: 1.3rem 2rem 1.5rem;
}
/* The page's root element (the route component, through App.vue's RouterView) gets the definite height. */
.layout-ai__page > :slotted(*) {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
